import { useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  ROTULO_FREQUENCIA_PLANO,
  type FiltroDeFatura,
  type ListaDePlanos,
  type PlanoNaLista,
  type ResumoDeCobrancas,
} from '@arena/shared/schemas';
import { diasEntre } from '@arena/shared/tempo';
import { dinheiro } from '../agenda/formatos.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { cobrancasApi, planosApi } from '../api/planos.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { BarrasHorizontais } from '../graficos/BarrasHorizontais.jsx';
import { Erro, EsqueletoDeCard, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { Botao, Card, Escolhas, Kpi, Nota, Status, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { mesPorExtenso } from './formatos.js';
import { ModalDeInadimplencia } from './ModalDeInadimplencia.jsx';
import { ModalDePagamento } from './ModalDePagamento.jsx';
import { ModalDePlano } from './ModalDePlano.jsx';
import { ModalDeReajuste } from './ModalDeReajuste.jsx';
import { TabelaDeFaturas } from './TabelaDeFaturas.jsx';

/**
 * `#v-planos` — os quatro KPIs, a tabela de faturas com os filtros e o
 * ranking de planos ativos.
 *
 * Os números vêm prontos do servidor, calculados sobre as linhas que o
 * perfil alcança. A tela só formata.
 *
 * Os modais abrem pela URL (`?fatura=`, `?plano=`, `?reajuste=1`,
 * `?inadimplencia=1`), como a ficha do aluno: um link colado abre a fatura
 * certa, e o voltar do navegador fecha.
 *
 * Ver: Arena_backend/Docs/plano/12-planos-faturas.md
 */

const FILTROS: readonly { valor: FiltroDeFatura; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'vencidas', rotulo: 'Vencidas' },
  { valor: 'a_vencer', rotulo: 'A vencer' },
  { valor: 'pagas', rotulo: 'Pagas' },
];

const CORES_DOS_PLANOS = [
  'var(--serie-1)',
  'var(--serie-2)',
  'var(--serie-3)',
  'var(--serie-4)',
  'var(--serie-5)',
];

export function PaginaDePlanos() {
  const { pode } = useSessao();
  const [params, setParams] = useSearchParams();

  const filtro = (FILTROS.find((f) => f.valor === params.get('filtro'))?.valor ??
    'todas') as FiltroDeFatura;
  const faturaAberta = params.get('fatura');
  const planoAberto = params.get('plano');
  const reajuste = params.get('reajuste') === '1';
  const inadimplencia = params.get('inadimplencia') === '1';

  function mudar(mudanca: (p: URLSearchParams) => void) {
    const novos = new URLSearchParams(params);
    mudanca(novos);
    setParams(novos, { replace: true });
  }

  const lePlanos = pode('planos', 'ler');
  const resumo = useQuery({
    queryKey: ['cobrancas', 'resumo'],
    queryFn: () => cobrancasApi.resumo(),
  });
  const planos = useQuery({
    queryKey: ['planos', 'lista'],
    queryFn: () => planosApi.listar(),
    enabled: lePlanos,
  });
  const faturas = useInfiniteQuery({
    queryKey: ['cobrancas', 'faturas', filtro],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      cobrancasApi.faturas({ filtro, cursor: pageParam, limite: 25 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  const itens = faturas.data?.pages.flatMap((p) => p.itens) ?? [];
  const planoEmEdicao = planos.data?.planos.find((p) => p.id === planoAberto);

  // "Gerar agora": a rotina da madrugada, na hora — para o Dono que cadastrou
  // contratos hoje e não quer esperar as 03:30. Idempotente como o job: sem
  // nada devido, não gera nada. Erro fica na tela, não em toast.
  const avisar = useToast();
  const cliente = useQueryClient();
  const [erroDaRotina, setErroDaRotina] = useState<string | null>(null);
  const rotina = useMutation({
    mutationFn: () => cobrancasApi.rotina(),
    onSuccess: () =>
      Promise.all([
        cliente.invalidateQueries({ queryKey: ['cobrancas'] }),
        cliente.invalidateQueries({ queryKey: ['planos'] }),
      ]),
  });

  async function gerarAgora() {
    setErroDaRotina(null);
    try {
      const resultado = await rotina.mutateAsync();
      avisar(
        resultado.geradas === 0
          ? 'Nenhuma fatura nova: as devidas até hoje já existem.'
          : `${resultado.geradas} ${resultado.geradas === 1 ? 'fatura gerada' : 'faturas geradas'}.`,
      );
    } catch (erro) {
      setErroDaRotina(mensagemDoErro(erro));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Planos e cobranças</h1>
          <p>
            {resumo.data
              ? `${mesPorExtenso(resumo.data.mes)} · ${resumo.data.aReceber.faturas} ${
                  resumo.data.aReceber.faturas === 1 ? 'fatura gerada' : 'faturas geradas'
                } automaticamente.`
              : 'As mensalidades do mês.'}
          </p>
        </div>
        <div className="head-actions">
          {pode('cobrancas', 'criar') && (
            <Botao
              variante="contorno"
              onClick={() => void gerarAgora()}
              disabled={rotina.isPending}
            >
              {rotina.isPending ? 'Gerando…' : 'Gerar faturas agora'}
            </Botao>
          )}
          {pode('planos', 'editar') && planos.data && planos.data.planos.length > 0 && (
            <Botao variante="contorno" onClick={() => mudar((p) => p.set('reajuste', '1'))}>
              Reajuste em lote
            </Botao>
          )}
          {pode('planos', 'criar') && (
            <Botao icone="plus" onClick={() => mudar((p) => p.set('plano', 'novo'))}>
              Novo plano
            </Botao>
          )}
        </div>
      </div>

      <Nota>
        <b>Planos e mensalidades.</b> As faturas nascem sozinhas conforme o dia de vencimento de
        cada assinatura. A régua de cobrança — três dias antes, no dia e depois do vencimento — é
        publicada a cada madrugada, e fica aqui para a recepção cobrar. Não há envio automático.
      </Nota>
      {erroDaRotina && (
        <div className="aviso conflito" role="alert">
          <Icone nome="info" />
          <div>{erroDaRotina}</div>
        </div>
      )}

      <Indicadores
        consulta={resumo}
        onInadimplencia={() => mudar((p) => p.set('inadimplencia', '1'))}
      />

      <div className="grid g-2-1">
        <Card
          titulo="Faturas"
          acao={
            <Escolhas
              aria-label="Filtrar faturas"
              opcoes={FILTROS}
              valor={filtro}
              onChange={(valor) => mudar((p) => p.set('filtro', valor))}
            />
          }
        >
          {faturas.isPending && <EsqueletoDeTabela colunas={7} />}
          {faturas.isError && (
            <Erro
              tipo={tipoDoErro(faturas.error)}
              detalhe={mensagemDoErro(faturas.error)}
              onTentarDeNovo={() => void faturas.refetch()}
            />
          )}
          {faturas.data && itens.length === 0 && <Vazio>Nenhuma fatura neste filtro.</Vazio>}
          {itens.length > 0 && (
            <>
              <TabelaDeFaturas
                faturas={itens}
                onAbrir={(id) => mudar((p) => p.set('fatura', id))}
              />
              {faturas.hasNextPage && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
                  <Botao
                    variante="contorno"
                    pequeno
                    onClick={() => void faturas.fetchNextPage()}
                    disabled={faturas.isFetchingNextPage}
                  >
                    {faturas.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
                  </Botao>
                </div>
              )}
            </>
          )}
        </Card>

        {lePlanos && (
          <CardDePlanos consulta={planos} onAbrir={(id) => mudar((p) => p.set('plano', id))} />
        )}
      </div>

      {faturaAberta && (
        <ModalDePagamento
          faturaId={faturaAberta}
          onFechar={() => mudar((p) => p.delete('fatura'))}
        />
      )}
      {planoAberto === 'novo' && <ModalDePlano onFechar={() => mudar((p) => p.delete('plano'))} />}
      {planoEmEdicao && (
        <ModalDePlano plano={planoEmEdicao} onFechar={() => mudar((p) => p.delete('plano'))} />
      )}
      {reajuste && planos.data && (
        <ModalDeReajuste
          planos={planos.data.planos}
          hoje={planos.data.hoje}
          onFechar={() => mudar((p) => p.delete('reajuste'))}
        />
      )}
      {inadimplencia && (
        <ModalDeInadimplencia
          onFechar={() => mudar((p) => p.delete('inadimplencia'))}
          onReceber={(id) =>
            mudar((p) => {
              p.delete('inadimplencia');
              p.set('fatura', id);
            })
          }
        />
      )}
    </>
  );
}

function Indicadores({
  consulta,
  onInadimplencia,
}: {
  consulta: UseQueryResult<ResumoDeCobrancas>;
  onInadimplencia: () => void;
}) {
  if (consulta.isPending) {
    return (
      <div className="grid g4">
        {[0, 1, 2, 3].map((i) => (
          <EsqueletoDeCard key={i} linhas={2} />
        ))}
      </div>
    );
  }
  if (consulta.isError) {
    return (
      <Erro
        tipo={tipoDoErro(consulta.error)}
        detalhe={mensagemDoErro(consulta.error)}
        onTentarDeNovo={() => void consulta.refetch()}
      />
    );
  }

  const r = consulta.data;
  const diasAteOUltimo = r.aVencer.ultimoVencimento
    ? diasEntre(r.hoje, r.aVencer.ultimoVencimento)
    : 0;
  return (
    <div className="grid g4">
      <Kpi
        valor={dinheiro(r.aReceber.valor)}
        rotulo="A receber no mês"
        tendencia="estavel"
        variacao={`${r.aReceber.faturas} ${r.aReceber.faturas === 1 ? 'fatura' : 'faturas'}`}
      />
      <Kpi
        valor={dinheiro(r.recebido.valorPago)}
        rotulo="Já recebido"
        tendencia={r.recebido.faturas > 0 ? 'alta' : 'estavel'}
        variacao={
          r.recebido.percentual === null
            ? 'nada a receber no mês'
            : `${r.recebido.percentual.toLocaleString('pt-BR')}% do previsto`
        }
      />
      <Kpi
        valor={dinheiro(r.aVencer.valor)}
        rotulo="A vencer"
        tendencia="estavel"
        variacao={
          r.aVencer.faturas === 0
            ? 'nada a vencer no mês'
            : diasAteOUltimo === 0
              ? 'vence hoje'
              : `nos próximos ${diasAteOUltimo} dias`
        }
      />
      <Kpi
        valor={dinheiro(r.vencido.valor)}
        rotulo="Vencido"
        tendencia={r.vencido.faturas > 0 ? 'baixa' : 'estavel'}
        variacao={`${r.vencido.alunos} ${r.vencido.alunos === 1 ? 'aluno' : 'alunos'}`}
        onClick={onInadimplencia}
      />
    </div>
  );
}

/**
 * "Planos ativos": o ranking pela receita de cada plano, e o cadastro
 * logo abaixo — clicar abre `#m-plano`.
 */
function CardDePlanos({
  consulta,
  onAbrir,
}: {
  consulta: UseQueryResult<ListaDePlanos>;
  onAbrir: (planoId: string) => void;
}) {
  if (consulta.isPending) {
    return (
      <Card titulo="Planos ativos">
        <EsqueletoDeCard linhas={4} />
      </Card>
    );
  }
  if (consulta.isError) {
    return (
      <Card titulo="Planos ativos">
        <Erro tipo={tipoDoErro(consulta.error)} detalhe={mensagemDoErro(consulta.error)} />
      </Card>
    );
  }

  const { planos, receitaRecorrente } = consulta.data;
  const ativos = planos.filter((p) => p.status === 'Ativo');
  const ranking = [...ativos].sort((a, b) => (b.alunos ?? 0) - (a.alunos ?? 0));

  return (
    <Card
      titulo="Planos ativos"
      dica={`${ativos.length} ${ativos.length === 1 ? 'produto' : 'produtos'}`}
    >
      {planos.length === 0 ? (
        <Vazio>Nenhum plano cadastrado ainda.</Vazio>
      ) : (
        <>
          {ranking.some((p) => p.alunos !== undefined) && (
            <BarrasHorizontais
              itens={ranking.map((plano, i) => ({
                nome: `${plano.nome} · ${dinheiro(plano.valor)}`,
                valor: plano.alunos ?? 0,
                rotulo: String(plano.alunos ?? 0),
                cor: CORES_DOS_PLANOS[i % CORES_DOS_PLANOS.length] as string,
                dica: dicaDoPlano(plano, receitaRecorrente ?? 0),
              }))}
            />
          )}
          <ul className="feed" style={{ marginTop: 14 }}>
            {planos.map((plano) => (
              <li
                key={plano.id}
                className="clickable"
                role="button"
                tabIndex={0}
                onClick={() => onAbrir(plano.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onAbrir(plano.id);
                  }
                }}
              >
                <div>
                  <b>{plano.nome}</b>
                  <small>
                    {ROTULO_FREQUENCIA_PLANO[plano.frequencia]} · vence dia {plano.diaVencimento}
                    {plano.pessoas > 1 ? ` · até ${plano.pessoas} pessoas` : ''}
                  </small>
                </div>
                <div className="rt">
                  <b className="num">{dinheiro(plano.valor)}</b>
                  {plano.status === 'Descontinuado' ? (
                    <Status situacao="neutra">Descontinuado</Status>
                  ) : plano.reajusteProgramado ? (
                    <small>
                      reajuste em {plano.reajusteProgramado.vigencia.slice(8, 10)}/
                      {plano.reajusteProgramado.vigencia.slice(5, 7)}
                    </small>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

function dicaDoPlano(plano: PlanoNaLista, receitaRecorrente: number): string {
  const receita = plano.receitaMensal ?? 0;
  const parte =
    receitaRecorrente > 0
      ? `<br><em>${Math.round((receita / receitaRecorrente) * 100)}% da receita recorrente</em>`
      : '';
  return `${dinheiro(receita)} por mês.${parte}`;
}

function tipoDoErro(erro: unknown): 'rede' | 'permissao' | 'falha' {
  if (erro instanceof ErroDeRede) return 'rede';
  if (erro instanceof ErroDeApi && erro.ehPermissao) return 'permissao';
  return 'falha';
}
